import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { CircularProgress } from '@mui/material';
import FormDialog from './questionGridDialog';
import { MdEditNote } from 'react-icons/md';
import { Button, Typography } from '@mui/material';
import DashboardNavbar from "../dashboardNavbar"
import DataTable from '../ui/DataTable';
import { StatusBadge } from '../ui/primitives';
import { LabelWithHelp } from '../ui/FieldHelp';
import { TRAINING_QUESTION_LIST_FIELD_HELP as HELP } from '../ui/listScreenFieldHelp';
import GroupedRowActionSheet, { ICON_PROPS } from '../ui/GroupedRowActionSheet';
import { Pencil, Check, X } from 'lucide-react';
import { useConfirm } from '../ui/ConfirmProvider';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const initialValue = { 
    courseId: '', 
    studentId: '', 
    courseName: '', 
    firstName: '', 
    lastName: '', 
    studentEmail: '', 
    question: '', 
    explanation: '', 
    hint: '', 
    keywords: '', 
    mimeType: 'Text', 
    asset: '', 
    answers: [] 
};

const QuestionsPage = () => {
    const askConfirmation = useConfirm();
    const { guardAction, lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
    const location = useLocation();
    const { trainingId } = location.state || {};
    const [loading, setLoading] = useState(false);
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    const [tableData, setTableData] = useState(null);
    const [open, setOpen] = useState(false);
    const [gridApi, setGridApi] = useState(null);
    const [formData, setFormData] = useState(initialValue);
    const menuItems = [];

    const getBasicAuthHeader = () => {
        const username = process.env.REACT_APP_API_USERNAME;
        const password = process.env.REACT_APP_API_PASSWORD;
        const encodedCredentials = btoa(`${username}:${password}`);
        return `Basic ${encodedCredentials}`;
    };

    useEffect(() => {
        getUsers();
    }, [trainingId]);

    const getUsers = async () => {
        if (trainingId) {
            setLoading(true);
            try {
                const response = await axios.get(apiUrl + `/api/training_questions/${trainingId}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': '*/*',
                        'Authorization': getBasicAuthHeader(),
                    },
                });
                setTableData(response.data);
            } catch (error) {
                //console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleFormSubmit = async () => {
        if (!guardAction('action.training.question.save')) return;
        if (!formData) return;
        // Save is an explicit action in the edit dialog; no typed-confirm gate (it
        // was unreachable behind the MUI dialog focus trap, so the update never fired).
        setLoading(true);
        const questionId = formData.questionId;
        const bodyData = {
            question: formData.question,
            active: formData.active,
            explanation: formData.explanation,
            hint: formData.hint,
            correctAnswer: formData.correctAnswer,
            keywords: formData.keywords,
            mimeType: formData.mimeType,
            asset: formData.asset,
            answer: (formData.answer || []).map((ans) => ({
                answer: ans.answer,
                is_correct: ans.is_correct,
            })),
        };
        try {
            await axios.put(`${apiUrl}/api/trainingquestion_update/${questionId}`, bodyData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': '*/*',
                    'Authorization': getBasicAuthHeader(),
                },
            });
        } catch (error) {
            //console.error('Error updating question:', error);
        } finally {
            setLoading(false);
            handleClose();
            getUsers();
        }
    };
    

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setFormData(initialValue);
    };

    const onChange = (e, index = null) => {
        const { id, value } = e.target;
        if (index !== null) {
            // Update incorrect answers (key is `answer`, matching the dialog + payload)
            const updatedAnswers = (formData.answer || []).map((answer, idx) =>
                idx === index ? { ...answer, answer: value } : answer
            );
            setFormData({ ...formData, answer: updatedAnswers });
        } else {
            // Update other fields
            setFormData({ ...formData, [id]: value });
        }
    };

    const handleUpdate = (oldData) => {
        if (!guardAction('action.training.question.edit')) return;
        setFormData(oldData);
        handleClickOpen();
    };

    const handleDelete = async (formData) => {
        if (!guardAction('action.training.question.approve')) return;
        let status = 1;
        if (formData.isDeleted === 1) {
            status = 0;
        }
        if (formData) {
            const confirmed = await askConfirmation({
              title: 'Delete question?',
              message: 'Are you sure you want to delete this question?',
              confirmLabel: 'Delete',
              variant: 'danger',
            });
            if (confirmed) {
                try {
                    const response = await axios.request({
                        method: 'delete',
                        url: `${apiUrl}/api/delete/${formData.questionId}`,
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': '*/*',
                            'Authorization': getBasicAuthHeader(),
                        },
                        data: { status }, // Sending status in the request body
                    });
                    //console.log('Question deleted successfully:', response);
                } catch (error) {
                    //console.error('Error deleting question:', error);
                } finally {
                    getUsers();
                }
            }
        }
    };
    

    const onGridReady = (params) => {
        setGridApi(params);
    };

    const columnDefs = [
        { headerName: '#', valueGetter: 'node.rowIndex + 1', checkboxSelection: true },
        { headerName: 'Question', field: 'question' },
        { 
            headerName: 'Correct Answer', 
            field: 'correctAnswer'
        },
        { headerName: 'Question Type', field: 'mimeType' },
        {
            headerName: 'Question Status',
            field: 'isDeleted',
            cellRenderer: (params) => {
                return params.value === 1 ? 'Disapproved' : 'Approved';
            },
        },
        {
            headerName: 'Actions',
            field: 'question_id',
            cellRenderer: (params) => (
                <div>
                    <MdEditNote
                        style={{ margin: '10px', fontSize: '1.5em', color: 'green', cursor: 'pointer' }}
                        onClick={() =>handleUpdate(params.data)}
                    >
                        Update
                    </MdEditNote>
                    <Button
                        variant="contained"
                        color="primary"
                        style={{
                            backgroundColor: 'purple',
                            color: 'white',
                            width: '12px',
                            height: '12px',
                            marginBottom: '30px',
                            fontSize: '8px', // Adjust the font size as needed
                            padding: '4px 10px', // Adjust the padding as needed
                        }}
                        onClick={() => handleDelete(params.data)}
                    >
                        {params.data.isDeleted === 1 ? 'Approve' : 'Disapprove'}
                    </Button>
                </div>
            ),
        },
    ];

    const defaultColDef = {
        sortable: true,
        flex: 1,
        filter: true,
    };

    return (
        <div>
                <DashboardNavbar menuItems={menuItems} />
            <Typography variant="h2" align="center" gutterBottom style={{ color: 'purple' }}>
                Questions
            </Typography>
            <div style={{ width: 'min(1180px, 95%)', margin: '0 auto' }}>
                <DataTable
                    rows={tableData || []}
                    loading={loading}
                    rowKey={(r, i) => r.questionId ?? r.question_id ?? i}
                    search
                    searchPlaceholder="Search questions, answers…"
                    searchKeys={['question', 'correctAnswer', 'mimeType']}
                    primary={{
                        header: '#',
                        label: (r) => r.question,
                        short: (r, i) => String(i + 1),
                    }}
                    columns={[
                        { header: 'Correct Answer', render: (r) => r.correctAnswer, muted: true },
                        { header: <LabelWithHelp help={HELP.questionType}>Question Type</LabelWithHelp>, render: (r) => r.mimeType, muted: true },
                        {
                            header: <LabelWithHelp help={HELP.status}>Status</LabelWithHelp>,
                            render: (r) => (
                                <StatusBadge $active={r.isDeleted !== 1}>
                                    {r.isDeleted === 1 ? 'Disapproved' : 'Approved'}
                                </StatusBadge>
                            ),
                        },
                    ]}
                    actionsColumnWidth={52}
                    actions={(r) => (
                        <GroupedRowActionSheet
                            entityLabel="Question"
                            title={r.question}
                            menuAriaLabel="Question actions"
                            layoutKey={`${r.questionId ?? r.id}-${r.isDeleted}`}
                            groupLabel="For this question"
                            groupItems={[
                                {
                                    key: 'toggle',
                                    icon: r.isDeleted === 1 ? <Check {...ICON_PROPS} /> : <X {...ICON_PROPS} />,
                                    label: r.isDeleted === 1 ? 'Approve' : 'Disapprove',
                                    ...lockProps('action.training.question.approve'),
                                },
                                {
                                    key: 'edit',
                                    icon: <Pencil {...ICON_PROPS} />,
                                    label: 'Edit',
                                    ...lockProps('action.training.question.edit'),
                                },
                            ]}
                            onAction={(key) => {
                                if (key === 'toggle') handleDelete(r);
                                else if (key === 'edit') handleUpdate(r);
                            }}
                        />
                    )}
                    emptyText="No questions yet."
                />
            </div>
            <FormDialog
                open={open}
                handleClose={handleClose}
                data={formData}
                onChange={onChange}
                handleFormSubmit={handleFormSubmit}
                loading={loading}
            />
            <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
        </div>
    );
};

export default QuestionsPage;
